import { useState, useEffect, useRef } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { SkeletonStatCards } from "@/clientWeb/components/ui/page-loader"
import { useNavigate } from "react-router-dom"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts"
import {
  Users, Download, ChevronDown, SmilePlus, Hand,
  TrendingUp, ArrowUpRight, BarChart2, PieChart as PieIcon,
  ArrowRight, ArrowLeft
} from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import { Button } from "@/clientWeb/components/ui/button"
import { NoData as NoDataComponent } from "@/clientWeb/components/ui/no-data"
import nodataAnalyticsImg from "@/assets/nodataanalytics.svg"
import { analyticsService, type AnalyticsRange, type AnalyticsSnapshot } from "./services/analytics.service"

const COLORS = ["#0ea5e9", "#8b5cf6", "#f97316", "#10b981", "#ec4899", "#f59e0b"]

const FILTER_TO_RANGE: Record<number, AnalyticsRange> = {
  0: 'today',
  1: 'yesterday',
  2: 'lastWeek',
  3: 'lastMonth',
}

// Data provided by analyticsService (see ./services/analytics.repo), which
// is empty until there is an analytics API.

// ── Helper components ────────────────────────────────────────────────────────

type ChartType = "bar" | "line" | "pie"

function FilterDropdown({ value, onChange, filters }: { value: string; onChange: (v: string) => void; filters: string[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-stroke-100 dark:border-border bg-card dark:bg-card rounded-lg px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:border-primary transition-colors"
      >
        {value} <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-20 bg-card dark:bg-card border border-stroke-100 dark:border-border rounded-xl shadow-lg min-w-[140px] py-1">
          {filters.map((f: string) => (
            <button
              key={f}
              onClick={() => { onChange(f); setOpen(false) }}
              className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                value === f
                  ? "text-primary font-semibold"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function StatCard({ title, value, iconBg, change, onClick, className }: {
  title: string
  value: string | number
  icon?: React.ReactNode
  iconBg: string
  change?: string
  onClick?: () => void
  className?: string
}) {
  const isPos = change ? !change.startsWith("-") && change !== "0" : true
  return (
    <button
      onClick={onClick}
      className={`group relative bg-card dark:bg-card border border-stroke-100 dark:border-border rounded-2xl px-4 py-4 shadow-xs hover:shadow-md transition-all duration-200 text-left hover:border-primary/30 w-full overflow-hidden ${className ?? ""}`}
    >
      <div className={`absolute inset-0 ${iconBg}`}>
        <svg
          className="absolute bottom-0 right-0 w-full h-full"
          viewBox="0 0 200 120"
          fill="none"
          preserveAspectRatio="none"
        >
          <path
            d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z"
            fill="currentColor"
            className="text-white/30 dark:text-white/10"
          />
        </svg>
      </div>
      <div className="relative z-10">
        <div className="flex absolute bottom-0 right-0 items-start justify-end mb-3">
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors" />
        </div>
        <div className="text-xl font-bold text-slate-900 dark:text-white mb-1">{value}</div>
        <div className="text-sm font-medium text-slate-600 dark:text-slate-400">{title}</div>
        {change && change !== "0" && (
          <div className={`inline-flex items-center gap-1 text-xs font-semibold mt-2 px-2 py-1 rounded-full ${
            isPos
              ? "bg-emerald-100/50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
              : "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400"
          }`}>
            <ArrowUpRight className={`w-3 h-3 ${!isPos ? "rotate-180" : ""}`} />
            {change}
          </div>
        )}
      </div>
    </button>
  )
}

function ChartTypeToggle({
  value,
  onChange,
  hasLine,
}: {
  value: ChartType
  onChange: (v: ChartType) => void
  hasLine?: boolean
}) {
  return (
    <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5">
      <button
        onClick={() => onChange("bar")}
        className={`p-1.5 rounded-md transition-colors ${
          value === "bar" ? "bg-card dark:bg-slate-600 shadow-xs" : "text-slate-400 hover:text-slate-600"
        }`}
      >
        <BarChart2 className="w-4 h-4" />
      </button>
      {hasLine && (
        <button
          onClick={() => onChange("line")}
          className={`p-1.5 rounded-md transition-colors ${
            value === "line" ? "bg-card dark:bg-slate-600 shadow-xs" : "text-slate-400 hover:text-slate-600"
          }`}
        >
          <TrendingUp className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={() => onChange("pie")}
        className={`p-1.5 rounded-md transition-colors ${
          value === "pie" ? "bg-card dark:bg-slate-600 shadow-xs" : "text-slate-400 hover:text-slate-600"
        }`}
      >
        <PieIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color }} className="mt-0.5">
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  )
}

function NoData() {
  return <NoDataComponent image={nodataAnalyticsImg} title="No data available" size="sm" showActionIcon={false} />
}

function MixedChart({
  data,
  chartType,
  dataKey,
  nameKey = "name",
}: {
  data: any[]
  chartType: ChartType
  dataKey: string | string[]
  nameKey?: string
}) {
  const axis = { stroke: "currentColor", className: "text-slate-400 dark:text-foreground", fontSize: 11 }

  if (!data?.length) return <NoData />

  if (chartType === "pie") {
    const flatData = Array.isArray(dataKey)
      ? data.map(d => ({ name: d[nameKey], value: d[dataKey[0]] }))
      : data.map(d => ({ name: d[nameKey], value: d[dataKey] }))
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={flatData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius="70%"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            labelLine={false}
            fontSize={11}
          >
            {flatData.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    )
  }

  if (chartType === "line") {
    const keys = Array.isArray(dataKey) ? dataKey : [dataKey]
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
          <XAxis dataKey={nameKey} {...axis} />
          <YAxis {...axis} />
          <Tooltip content={<CustomTooltip />} />
          {keys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i]} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    )
  }

  const keys = Array.isArray(dataKey) ? dataKey : [dataKey]
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={28}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
        <XAxis dataKey={nameKey} {...axis} />
        <YAxis {...axis} />
        <Tooltip content={<CustomTooltip />} />
        {keys.map((k, i) => (
          <Bar key={k} dataKey={k} fill={COLORS[i]} radius={[4, 4, 0, 0]} name={k} />
        ))}
        {keys.length > 1 && <Legend />}
      </BarChart>
    </ResponsiveContainer>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AnalyticsDashboard() {
  const t = useTranslation()
  const navigate = useNavigate()

  const FILTERS = [
    t("analytics.filterToday"),
    t("analytics.filterYesterday"),
    t("analytics.filterLastWeek"),
    t("analytics.filterLastMonth"),
  ]

  const [filter, setFilter] = useState(FILTERS[0])
  const [insightChartType, setInsightChartType] = useState<ChartType>("bar")
  const [featuresChartType, setFeaturesChartType] = useState<ChartType>("bar")
  const [deviceChartType, setDeviceChartType] = useState<ChartType>("pie")
  const [engageChartType, setEngageChartType] = useState<ChartType>("bar")
  const [isLoading, setIsLoading] = useState(true)
  const [isPrinting, setIsPrinting] = useState(false)
  const [snapshot, setSnapshot] = useState<AnalyticsSnapshot | null>(null)

  const filterIdx = Math.max(0, FILTERS.indexOf(filter))

  // Fetch snapshot whenever the active filter changes
  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    analyticsService
      .getSnapshot(FILTER_TO_RANGE[filterIdx])
      .then((snap) => { if (!cancelled) setSnapshot(snap) })
      .catch((err) => {
        if (cancelled) return
        toast.error({ title: 'Analytics error', description: err instanceof Error ? err.message : 'Failed to load' })
      })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [filterIdx])

  const currentStats = snapshot?.stats ?? { live: 0, total: 0, minutes: '0 mins', cancelled: 0, attendees: '0', recordings: 0, avgDuration: '0 mins', changes: ['', '', '', '', '', '', ''] }
  const currentInsights = snapshot?.insights ?? []
  const currentTopUsers = snapshot?.topUsers ?? []
  const currentFeatures = snapshot?.features ?? []
  const currentDevices = snapshot?.devices ?? []
  const currentEngage = snapshot?.engagement ?? { emojis: 0, handRaise: 0, polls: 0 }
  // Three zero bars are not data: show the empty state instead.
  const hasEngagement = currentEngage.emojis + currentEngage.handRaise + currentEngage.polls > 0
  const currentEngageData = hasEngagement
    ? [
        { name: "Emojis",     value: currentEngage.emojis    },
        { name: "Hand Raise", value: currentEngage.handRaise },
        { name: "Polls",      value: currentEngage.polls     },
      ]
    : []
  const currentLocation = snapshot?.locations ?? []

  const handleExportPDF = () => {
    setIsPrinting(true)
    setTimeout(() => {
      document.body.classList.add("printing-analytics")
      window.print()
      document.body.classList.remove("printing-analytics")
      setIsPrinting(false)
    }, 200)
  }

  const stats = [
    {
      title: t("analytics.liveMeetings"),
      value: currentStats.live,
      iconBg: "bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/20 dark:to-green-900/20",
      change: currentStats.changes[0],
      route: "/analytics/live-meetings",
    },
    {
      title: t("analytics.totalMeetings"),
      value: currentStats.total,
      iconBg: "bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20",
      change: currentStats.changes[1],
      route: "/analytics/meetings?type=total",
    },
    {
      title: t("analytics.totalMinutes"),
      value: currentStats.minutes,
      iconBg: "bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20",
      change: currentStats.changes[2],
    },
    {
      title: t("analytics.cancelled"),
      value: currentStats.cancelled,
      iconBg: "bg-gradient-to-br from-red-50 to-pink-100 dark:from-red-900/20 dark:to-pink-900/20",
      change: currentStats.changes[3],
    },
    {
      title: t("analytics.attendees"),
      value: currentStats.attendees,
      iconBg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20",
      change: currentStats.changes[4],
    },
    {
      title: t("analytics.totalRecordings"),
      value: currentStats.recordings,
      iconBg: "bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/20 dark:to-violet-900/20",
      change: currentStats.changes[5],
      route: "/analytics/recordings",
    },
    {
      title: t("analytics.averageDuration"),
      value: currentStats.avgDuration,
      iconBg: "bg-gradient-to-br from-sky-50 to-cyan-100 dark:from-sky-900/20 dark:to-cyan-900/20",
      change: currentStats.changes[6],
    },
  ]

  return (
    <div className="flex flex-col h-full overflow-y-auto w-full p-4 md:p-6 gap-5 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 dark:bg-[#212121] dark:from-transparent dark:via-transparent dark:to-transparent">

      {/* Breadcrumb + Header */}
      <div className="relative">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <button
            onClick={() => navigate("/meetings")}
            className="hover:text-foreground text-primary font-semibold flex items-center gap-1 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" /> {t("common.dashboard")}
          </button>
          <span>/</span>
          <span className="text-foreground font-medium">{t("analytics.title")}</span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-3">
            <h1 className="text-lg font-bold text-foreground">{t("analytics.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("analytics.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <FilterDropdown value={filter} onChange={setFilter} filters={FILTERS} />
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleExportPDF} disabled={isPrinting}>
              <Download className="w-3.5 h-3.5" />
              {isPrinting ? t("analytics.preparing") : t("analytics.exportPDF")}
            </Button>
          </div>
        </div>
      </div>

      {/* Stat Cards — auto-fit grid: all cards in one row when there's space,
          wraps to next line when the viewport narrows, never compresses below 170px. */}
      {isLoading ? (
        <SkeletonStatCards count={stats.length} />
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}
        >
          {stats.map((s) => (
            <StatCard
              key={s.title}
              {...s}
              onClick={s.route ? () => navigate(s.route!) : undefined}
            />
          ))}
        </div>
      )}

      {/* Quick Access Links */}
      {!isLoading && (
        <div className="flex flex-wrap gap-2">
          {[
            { label: `🏆 ${t("analytics.topUsers")}`,    route: "/analytics/top-users" },
            { label: `🌍 ${t("analytics.worldMap")}`,    route: "/analytics/world-map" },
            { label: `📹 ${t("meetings.recordings")}`,   route: "/analytics/recordings" },
            { label: `📊 ${t("analytics.allMeetings")}`, route: "/analytics/meetings" },
            { label: `⏱ ${t("analytics.standups")}`,     route: "/analytics/standup" },
          ].map(({ label, route }) => (
            <button
              key={route}
              onClick={() => navigate(route)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border border-border bg-card text-foreground hover:bg-muted transition-all hover:scale-105 active:scale-95"
            >
              {label} →
            </button>
          ))}
        </div>
      )}

      {/* Charts Row 1 */}
      {!isLoading && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Meeting Insights */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-2xl" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl opacity-60" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-emerald-400/20 to-blue-400/20 rounded-full blur-2xl opacity-40" />
          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                  <h3 className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.meetingInsights")}</h3>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.meetingsOverTime")}</p>
              </div>
              <ChartTypeToggle value={insightChartType} onChange={setInsightChartType} hasLine />
            </div>
            <div className="h-[220px]">
              <MixedChart data={currentInsights} chartType={insightChartType} dataKey="meetings" nameKey="date" />
            </div>
          </div>
        </div>

        {/* Top Users */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-2xl" />
          <div className="absolute top-0 left-0 w-28 h-28 bg-gradient-to-br from-emerald-400/20 to-teal-400/20 rounded-full blur-3xl opacity-60" />
          <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-2xl opacity-40" />
          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />
                  <h3 className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.top10Users")}</h3>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.byTimeSpent")}</p>
              </div>
              <button
                onClick={() => navigate("/analytics/top-users")}
                className="px-3 py-1.5 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
              >
                {t("analytics.viewAll")}
              </button>
            </div>
            {currentTopUsers.length === 0 ? <NoData /> : (
            <div className="overflow-auto max-h-[220px] rounded-xl">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="border-b border-slate-200 dark:border-slate-600/30">
                    <TableHead className="text-left py-3 text-slate-600 dark:text-slate-300 font-semibold">{t("analytics.name")}</TableHead>
                    <TableHead className="text-left py-3 text-slate-600 dark:text-slate-300 font-semibold">{t("analytics.role")}</TableHead>
                    <TableHead className="text-left py-3 text-slate-600 dark:text-slate-300 font-semibold hidden sm:table-cell">{t("analytics.email")}</TableHead>
                    <TableHead className="text-right py-3 text-slate-600 dark:text-slate-300 font-semibold">{t("analytics.time")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentTopUsers.map((u, i) => (
                    <TableRow key={i} className="border-b border-stroke-100 dark:border-slate-700/30 hover:bg-card/50 dark:hover:bg-slate-700/30 transition-colors">
                      <TableCell className="py-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary via-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-lg">
                          {u.name[0]}
                        </div>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[90px]">{u.name}</span>
                      </TableCell>
                      <TableCell className="py-3 text-slate-600 dark:text-slate-300">{u.role}</TableCell>
                      <TableCell className="py-3 text-slate-500 dark:text-slate-400 hidden sm:table-cell truncate max-w-[100px]">{u.email}</TableCell>
                      <TableCell className="py-3 text-right font-bold text-slate-800 dark:text-slate-200">{u.timeSpent} min</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Charts Row 2 */}
      {!isLoading && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Most Used Features */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-2xl" />
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-400/20 to-red-400/20 rounded-full blur-3xl opacity-50" />
          <div className="relative z-10 p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500" />
                  <h3 className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.mostUsedFeatures")}</h3>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.screenVideoDocShare")}</p>
              </div>
              <ChartTypeToggle value={featuresChartType} onChange={setFeaturesChartType} />
            </div>
            <div className="flex flex-1 min-h-[180px]">
              <MixedChart data={currentFeatures} chartType={featuresChartType} dataKey="value" nameKey="name" />
            </div>
          </div>
        </div>

        {/* Device Utilization */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-2xl" />
          <div className="absolute bottom-0 left-0 w-28 h-28 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full blur-3xl opacity-50" />
          <div className="relative z-10 p-6 flex flex-col h-full">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500" />
                  <h3 className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.deviceUtilization")}</h3>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.mobileTabletDesktop")}</p>
              </div>
              <ChartTypeToggle value={deviceChartType} onChange={setDeviceChartType} />
            </div>
            <div className="flex flex-1 min-h-[180px]">
              <MixedChart data={currentDevices} chartType={deviceChartType} dataKey="value" nameKey="name" />
            </div>
            {deviceChartType === "bar" && (
              <div className="flex items-center justify-around mt-4 p-3 bg-card/30 dark:bg-slate-700/30 rounded-xl backdrop-blur-sm">
                {currentDevices.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: COLORS[i] }} />
                    <span className="text-xs font-medium text-foreground">
                      {d.name} <strong className="text-slate-800 dark:text-white">{d.value}%</strong>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Engagement Insights */}
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-2xl" />
          <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-pink-400/20 to-purple-400/20 rounded-full blur-3xl opacity-50" />
          <div className="relative z-10 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500" />
                  <h3 className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.engagementInsights")}</h3>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.emojisHandRaisePolls")}</p>
              </div>
              <ChartTypeToggle value={engageChartType} onChange={setEngageChartType} />
            </div>
            <div className="h-[180px]">
              <MixedChart data={currentEngageData} chartType={engageChartType} dataKey="value" nameKey="name" />
            </div>
            <div className="flex items-center justify-around mt-4 p-3 bg-card/30 dark:bg-slate-700/30 rounded-xl backdrop-blur-sm">
              {[
                { label: "Emojis",     icon: <SmilePlus className="w-3.5 h-3.5 text-amber-500" />,  val: currentEngage.emojis    },
                { label: "Hand Raise", icon: <Hand      className="w-3.5 h-3.5 text-blue-500" />,   val: currentEngage.handRaise },
                { label: "Polls",      icon: <Users     className="w-3.5 h-3.5 text-purple-500" />, val: currentEngage.polls     },
              ].map(e => (
                <div key={e.label} className="flex items-center gap-2">
                  {e.icon}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{t("analytics.total")}</p>
                    <p className="text-2xl font-bold text-foreground">{e.val}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Location Chart */}
      {!isLoading && (
      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-white/40 dark:from-transparent dark:via-transparent dark:to-transparent dark:bg-card backdrop-blur-xl rounded-2xl border border-white/20 dark:border-border shadow-2xl" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/20 to-emerald-400/20 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-400/20 to-cyan-400/20 rounded-full blur-2xl opacity-40" />
        <div className="relative z-10 p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500" />
              <h3 className="text-base font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("analytics.meetingsByLocation")}</h3>
            </div>
            {/* <p className="text-sm text-slate-500 dark:text-slate-400">{t("analytics.meetingOverview")}</p> */}
          </div>
          <div className="h-[320px]">
            {currentLocation.length === 0 ? <NoData /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={currentLocation} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                <XAxis dataKey="country" fontSize={11} stroke="currentColor" className="text-slate-400" />
                <YAxis fontSize={11} stroke="currentColor" className="text-slate-400" />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="meetings" name="Meetings" radius={[4, 4, 0, 0]}>
                  {currentLocation.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
      )}

    </div>
  )
}

export { AnalyticsDashboard }
